import countries from './maps/countries.json';
import { formatAmount, formatTick, unitOf } from './units';

export const WORLD_MAP_NAME = 'evolve-world';
const normalize = (value) => typeof value === 'string'
  ? value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/ё/g, 'е').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru') : '';
const byId = new Map(countries.map((country) => [country.id, country]));
const aliases = new Map();
for (const country of countries) {
  for (const name of country.aliases) {
    const alias = normalize(name);
    if (!aliases.has(alias)) aliases.set(alias, new Set());
    aliases.get(alias).add(country.id);
  }
}

/** Only exact, unambiguous ISO/name aliases; never guesses a country from a city. */
export function countryOf(value) {
  const matches = aliases.get(normalize(value));
  return matches?.size === 1 ? byId.get([...matches][0]) : null;
}

function tint(surface, accent) {
  if (![surface, accent].every((value) => /^#[\da-f]{6}$/i.test(value))) return accent;
  const mix = (start) => Math.round(parseInt(surface.slice(start, start + 2), 16) * 0.72 + parseInt(accent.slice(start, start + 2), 16) * 0.28).toString(16).padStart(2, '0');
  return `#${mix(1)}${mix(3)}${mix(5)}`;
}

/** Bind one actual aggregated row to a country; unmatched rows stay visible. */
export function buildWorldMap(chart, columns, rows, palette, theme) {
  const x = columns.find((column) => column.key === chart.x);
  const y = columns.find((column) => column.key === chart.y[0]);
  const bad = (error = 'shape') => ({ option: null, error });
  if (chart.y.length !== 1 || x?.kind !== 'dimension' || x?.type !== 'string'
    || y?.kind !== 'measure' || y?.type !== 'number' || chart.series != null || chart.size != null
    || columns.some((column) => column.kind !== 'measure' && column.key !== chart.x)) return bad();
  const seen = new Set();
  const data = [];
  const unmatched = [];
  for (const row of rows) {
    const value = row[y.key];
    if (value != null && (typeof value !== 'number' || !Number.isFinite(value))) return bad();
    const country = countryOf(row[x.key]);
    if (!country) { unmatched.push(row); continue; }
    if (seen.has(country.id)) return bad('country_duplicate');
    seen.add(country.id);
    data.push({ name: country.id, value });
  }
  const values = data.map((item) => item.value).filter((value) => typeof value === 'number');
  if (!data.length) return bad('country');
  if (!values.length) return bad('country_data');
  const min = Math.min(...values);
  const max = Math.max(...values);
  const text = { fontFamily: theme.fontFamily, color: theme.muted, fontSize: 12, textBorderWidth: 0, textShadowBlur: 0 };
  return {
    units: [unitOf(y)], unmatched,
    mapLabels: Object.fromEntries(countries.map((country) => [country.id, country.label])),
    option: {
      legend: { show: false },
      visualMap: {
        min, max, dimension: 0, seriesIndex: 0, calculable: false,
        orient: 'horizontal', left: 'center', bottom: 0, itemWidth: 12,
        textGap: 10, textStyle: text,
        // A degenerate continuous scale also colors NaN regions in ECharts.
        // An exact-value piece keeps null/absent countries neutral.
        ...(min === max ? {
          type: 'piecewise', itemHeight: 12,
          pieces: [{ value: min, label: formatAmount(min, y), color: palette[0] }],
        } : {
          type: 'continuous', itemHeight: 150,
          text: [formatTick(max, y), formatTick(min, y)],
          inRange: { color: [tint(theme.surface, palette[0]), palette[0]] },
        }),
        outOfRange: { color: theme.raised },
        formatter: (value) => formatAmount(value, y),
      },
      series: [{
        id: 'bound-series-0', name: y.title || y.key, type: 'map', map: WORLD_MAP_NAME,
        nameProperty: 'name', mapValueCalculation: 'average', selectedMode: false,
        showLegendSymbol: false,
        roam: 'move', scaleLimit: { min: 1, max: 8 }, left: 8, right: 8, top: 8, bottom: 48,
        data,
        itemStyle: { areaColor: theme.raised, borderColor: theme.surface, borderWidth: 0.7 },
        label: { ...text, show: false, formatter: (item) => byId.get(item.name)?.label || item.name },
        emphasis: {
          label: { ...text, color: theme.ink, show: false },
          itemStyle: { borderColor: theme.ink, borderWidth: 1, shadowBlur: 0 },
        },
      }],
    },
  };
}
